'use client';

import { Button } from '@ahafood/ui/components/button';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { Camera, Loader2, Upload, X } from 'lucide-react';
import React from 'react';
import {
  useFileUpload,
  isAndroid,
  type FileWithPreview,
} from '@/hooks/use-file-upload';

interface ImagePreviewItemProps {
  src: string;
  onRemove?: () => void;
  disabled?: boolean;
  isCompressing?: boolean;
}

const ImagePreviewItem = ({
  src,
  onRemove,
  disabled,
  isCompressing,
}: ImagePreviewItemProps) => (
  <div className="relative aspect-square group">
    <img
      src={src ?? `${assetPrefix}/static/images/empty-store-bot.webp`}
      alt="Ảnh"
      className="size-full object-cover rounded-md bg-neutral-10"
    />
    {onRemove && !disabled && !isCompressing && (
      <Button
        onClick={onRemove}
        size="icon"
        type="button"
        variant="destructive"
        className="absolute -top-1 -right-1 size-5 rounded-full shadow-sm bg-neutral-10 text-neutral-60"
      >
        <X className="size-3" />
      </Button>
    )}
  </div>
);

interface RatingImageUploadProps {
  onFilesChange?: (files: File[]) => void;
  disabled?: boolean;
  existingImages?: string[];
  onDeleteExistingImage?: (imageUrl: string) => void;
}

export function RatingImageUpload({
  onFilesChange,
  disabled = false,
  existingImages = [],
  onDeleteExistingImage,
}: RatingImageUploadProps) {
  const maxSizeMB = 1;
  const maxSize = maxSizeMB * 1024 * 1024;
  const maxFiles = 5;

  const [
    { files, isDragging, errors, isCompressing },
    {
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      openCameraDialog,
      removeFile,
      getInputProps,
    },
  ] = useFileUpload({
    accept: 'image/*',
    maxSize,
    multiple: true,
    maxFiles,
    onFilesChange: (updatedFiles: FileWithPreview[]) => {
      const actualFiles = updatedFiles
        .map((f) => f.file)
        .filter((file): file is File => file instanceof File);
      onFilesChange?.(actualFiles);
    },
  });

  const isAndroidDevice = isAndroid();

  const totalImages = existingImages.length + files.length;

  const handleRemoveExisting = (url: string) => onDeleteExistingImage?.(url);

  const handleRemoveNew = (id: string) => removeFile(id);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-neutral-90">Chia sẻ đơn hàng</h3>
        <span className="text-xs text-neutral-40">
          {totalImages}/{maxFiles} ảnh, tối đa {maxSizeMB}MB
        </span>
      </div>

      <div
        onDragEnter={disabled ? undefined : handleDragEnter}
        onDragLeave={disabled ? undefined : handleDragLeave}
        onDragOver={disabled ? undefined : handleDragOver}
        onDrop={disabled ? undefined : handleDrop}
        className={cn(
          'relative border border-dashed rounded-lg transition-colors',
          'hover:bg-neutral-5 focus-within:border-primary-40',
          isDragging && 'border-primary-40 bg-primary-5',
          (disabled || isCompressing) && 'opacity-50 cursor-not-allowed',
          totalImages === 0 && 'h-20 flex items-center justify-center'
        )}
      >
        <input
          {...getInputProps()}
          className="sr-only"
          disabled={disabled || isCompressing}
        />

        {totalImages === 0 ? (
          <div className="h-24 flex flex-col items-center justify-center">
            <EmptyState
              isAndroidDevice={isAndroidDevice}
              disabled={disabled}
              isCompressing={isCompressing}
              openFileDialog={openFileDialog}
              openCameraDialog={openCameraDialog}
            />
          </div>
        ) : (
          <div className="p-3">
            <div className="grid grid-cols-3 gap-2">
              {/* Render tất cả ảnh hiện có */}
              {existingImages.map((url, i) => (
                <ImagePreviewItem
                  key={`existing-${i}`}
                  src={url}
                  onRemove={
                    onDeleteExistingImage
                      ? () => handleRemoveExisting(url)
                      : undefined
                  }
                  disabled={disabled}
                  isCompressing={isCompressing}
                />
              ))}

              {files.map((fileItem) => (
                <ImagePreviewItem
                  key={fileItem.id}
                  src={fileItem.preview ?? ''}
                  onRemove={() => handleRemoveNew(fileItem.id)}
                  disabled={disabled}
                  isCompressing={isCompressing}
                />
              ))}

              {/* Add Button */}
              {totalImages < maxFiles && (
                <AddImageButtons
                  isAndroidDevice={isAndroidDevice}
                  disabled={disabled}
                  isCompressing={isCompressing}
                  openFileDialog={openFileDialog}
                  openCameraDialog={openCameraDialog}
                  maxFiles={maxFiles}
                  filesLength={files.length}
                />
              )}

              {/* Loading */}
              {isCompressing && (
                <div className="aspect-square border-2 border-dashed border-neutral-30 rounded-md flex flex-col items-center justify-center">
                  <Loader2 className="size-5 mb-1 animate-spin text-primary-50" />
                  <span className="text-xs text-neutral-50">Đang xử lý...</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="space-y-1">
          {errors.map((error, index) => (
            <div
              key={index}
              className="flex items-center gap-2 text-xs text-red-50"
            >
              <X className="size-3 shrink-0" />
              <span>{error}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface EmptyStateProps {
  isAndroidDevice: boolean;
  disabled: boolean;
  isCompressing: boolean;
  openFileDialog: () => void;
  openCameraDialog: () => void;
}

function EmptyState({
  isAndroidDevice,
  disabled,
  isCompressing,
  openFileDialog,
  openCameraDialog,
}: EmptyStateProps) {
  if (isCompressing) {
    return (
      <>
        <Loader2 className="size-6 text-primary-50 mb-2 animate-spin" />
        <span className="text-sm text-neutral-50">Đang xử lý ảnh...</span>
      </>
    );
  }

  const commonClass =
    'flex flex-col items-center px-4 py-2 hover:bg-neutral-5 transition-colors disabled:cursor-not-allowed rounded-md';

  if (isAndroidDevice) {
    return (
      <div className="flex gap-4">
        <button
          type="button"
          onClick={openFileDialog}
          disabled={disabled}
          className={commonClass}
        >
          <Upload className="size-6 text-neutral-50 mb-2" />
          <span className="text-sm text-neutral-50">Upload ảnh</span>
        </button>
        <button
          type="button"
          onClick={openCameraDialog}
          disabled={disabled}
          className={commonClass}
        >
          <Camera className="size-6 text-neutral-50 mb-2" />
          <span className="text-sm text-neutral-50">Chụp ảnh</span>
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={openFileDialog}
      disabled={disabled}
      className="size-full flex flex-col items-center justify-center hover:bg-neutral-5 transition-colors disabled:cursor-not-allowed"
    >
      <Camera className="size-6 text-neutral-50 mb-2" />
      <span className="text-sm text-neutral-50">Thêm ảnh</span>
    </button>
  );
}

interface AddImageButtonsProps {
  isAndroidDevice: boolean;
  disabled: boolean;
  isCompressing: boolean;
  openFileDialog: () => void;
  openCameraDialog: () => void;
  maxFiles: number;
  filesLength: number;
}

function AddImageButtons({
  isAndroidDevice,
  disabled,
  isCompressing,
  openFileDialog,
  openCameraDialog,
  maxFiles,
  filesLength,
}: AddImageButtonsProps) {
  if (filesLength >= maxFiles || disabled || isCompressing) {
    return null;
  }

  const buttonClass = cn(
    'aspect-square border-2 border-dashed border-neutral-30 rounded-md',
    'flex flex-col items-center justify-center hover:bg-neutral-5 transition-colors',
    'text-neutral-50 hover:text-neutral-70'
  );

  if (isAndroidDevice) {
    return (
      <>
        <button type="button" onClick={openFileDialog} className={buttonClass}>
          <Upload className="size-4 mb-1" />
          <span className="text-xs">Upload</span>
        </button>
        <button
          type="button"
          onClick={openCameraDialog}
          className={buttonClass}
        >
          <Camera className="size-4 mb-1" />
          <span className="text-xs">Chụp</span>
        </button>
      </>
    );
  }

  return (
    <button type="button" onClick={openFileDialog} className={buttonClass}>
      <Camera className="size-5 mb-1" />
      <span className="text-xs">Thêm ảnh</span>
    </button>
  );
}
